// AgentNodeModal.js
import React, { useState, useEffect, useRef } from "react";
import Modal from "../base/Modal"; // Assuming Modal base component path

const AgentNodeModal = ({
  mode,
  nodeData,
  onClose,
  onSave,
  onDelete,
  agentsList = [], // Default to empty array
  viewOnly,
}) => {
  const [selectedAgentName, setSelectedAgentName] = useState(
    nodeData?.data?.agent_name || ""
  );
  const selectRef = useRef(null);

  // Memoize selectedAgentDetails to avoid re-calculating on every render
  const selectedAgentDetails = React.useMemo(() => {
    return agentsList?.find(agent => agent.name === selectedAgentName);
  }, [agentsList, selectedAgentName]);

  useEffect(() => {
    // If in edit mode and an agent_name exists but details aren't found (e.g. agentsList changed)
    // it might be desirable to clear the selection or show a warning.
    // For now, we just ensure focus.
    if (!viewOnly && selectRef.current && !selectedAgentName) {
      selectRef.current.focus();
    }
  }, [viewOnly, selectedAgentName]);

  const handleSelectAgent = (agentName) => {
    setSelectedAgentName(agentName);
  };

  const handleSave = () => {
    if (!selectedAgentName || !selectedAgentDetails) { // Ensure details are also available
      alert("Please select a valid agent."); // Or use a toast notification
      return;
    }
    const updatedNode = {
      ...nodeData,
      data: {
        ...nodeData?.data, // Preserve other data if any
        agent_name: selectedAgentName,
        agent_description: selectedAgentDetails.description || "", // Save the description
      },
    };
    onSave(updatedNode);
  };

  return (
    <Modal
      title={viewOnly ? "Agent Details" : mode === "add" ? "Add Agent Node" : "Edit Agent Node"}
      isOpen={true}
      onClose={onClose}
    >
      <div className="p-4 h-[auto] max-h-[70vh] overflow-y-auto bg-zinc-900 rounded-lg shadow-md">
        <div className="p-4">
          <label
            className="block text-sm font-medium text-zinc-200 mb-2"
            htmlFor="agent-select"
          >
            {viewOnly ? "Selected Agent" : "Select an Agent"}
          </label>

          {/* Simplified display for selected agent when not in viewOnly mode */}
          {selectedAgentName && !viewOnly ? (
            <div className="flex items-center justify-between mb-4 border border-zinc-700 bg-zinc-800 p-3 rounded">
              <span className="text-zinc-100 font-medium">
                {selectedAgentName}
              </span>
              <button
                className="px-3 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded transition-colors text-sm"
                onClick={() => setSelectedAgentName("")} // Clear selection to show dropdown again
                aria-label="Change selected agent"
              >
                Change
              </button>
            </div>
          ) : !viewOnly ? (
            <select
              id="agent-select"
              ref={selectRef}
              className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-2.5 rounded focus:outline-none focus:ring-2 focus:ring-[#5652CF] transition-colors mb-4"
              onChange={(e) => handleSelectAgent(e.target.value)}
              value={selectedAgentName} // Ensure this is controlled
              aria-label="Select an agent"
            >
              <option value="" disabled={selectedAgentName !== ""}> {/* Disable if already selected to prevent accidental reselection of placeholder */}
                -- Select an Agent --
              </option>
              {agentsList.map((agent) => (
                <option key={agent.name} value={agent.name}>
                  {agent.name}
                </option>
              ))}
            </select>
          ) : null}

          {/* Display agent details (name and description) */}
          {selectedAgentDetails && (
            <div className="mb-4">
              <div className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-3 rounded flex flex-col space-y-1 transition-all duration-200">
                <h3 className="text-md font-semibold text-blue-400">{selectedAgentDetails.name}</h3>
                <p className="text-sm text-zinc-300 whitespace-pre-wrap"> {/* Allow text wrapping for description */}
                  {selectedAgentDetails.description || "No description available."}
                </p>
              </div>
            </div>
          )}
          
          {/* Message if viewing and no details are found (e.g., agent was deleted from list) */}
          {viewOnly && !selectedAgentDetails && selectedAgentName && (
             <p className="text-yellow-500 text-sm">
               Details for agent "{selectedAgentName}" are currently unavailable. It might have been removed.
             </p>
          )}
           {viewOnly && !selectedAgentName && (
             <p className="text-zinc-400">No agent selected for this node.</p>
          )}


          {!viewOnly && (
            <div className="flex justify-end gap-3 mt-6">
              {mode === "edit" && (
                <button
                  className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 border border-red-500 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
                  onClick={() => onDelete(nodeData.id)}
                  aria-label="Delete agent node"
                >
                  Delete Node
                </button>
              )}
              <button
                className="px-4 py-2 text-white bg-blue-600 hover:bg-[#5652CF] border border-[#5652CF] rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
                onClick={handleSave}
                disabled={!selectedAgentName || !selectedAgentDetails} // Disable if no agent or details are missing
                aria-label="Save agent node"
              >
                {mode === "add" ? "Add Node" : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AgentNodeModal;